import { CheckIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";

import { formatJSON, isValidJSON } from "./utils";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileDisplay from "@/components/ui/FileDisplay";
import { FileCollection } from "@/components/ui/FileDisplay/FileDisplayContent/FileCollection";
import { DetectedFileItem } from "@/components/ui/FileDisplay/utils";
import { Textarea } from "@/components/ui/textarea";
import { Variable } from "@/components/InputVariableConfigurator/types";

const MultipleFilesView = ({
  isPopoverOpen,
  setIsPopoverOpen,
  onSaveFile,
  fileItems,
  onDeleteFile,
  onAddFiles,
  readOnly,
  popoverPortalContainer,
}: {
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
  onSaveFile?: (newData: string, previousData: string) => void;
  fileItems: DetectedFileItem[];
  onDeleteFile?: (index: number) => void;
  onAddFiles?: (fileUrls: string[]) => void;
  readOnly: boolean;
  popoverPortalContainer?: HTMLElement | null;
}) => (
  <div className="grid max-h-96 grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2">
    <FileCollection
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
      saveData={!readOnly ? onSaveFile : undefined}
      fileCollectionCount={fileItems.length}
      detectedFileValues={fileItems.map(item => item.data || "")}
      onDeleteFile={!readOnly ? onDeleteFile : undefined}
      onAddFiles={!readOnly ? onAddFiles : undefined}
      showDeleteButtons={!readOnly}
      popoverPortalContainer={popoverPortalContainer}
    />
  </div>
);

const SingleFileView = ({
  fileItems,
  onDeleteFile,
  readOnly,
}: {
  fileItems: DetectedFileItem[] | null;
  onDeleteFile?: (index: number) => void;
  readOnly: boolean;
}) => (
  <div className="flex max-h-96 items-center justify-center overflow-y-auto">
    <FileDisplay
      data={fileItems?.[0]?.data || ""}
      className="w-full max-w-lg"
      onDelete={!readOnly ? () => onDeleteFile?.(0) : undefined}
      showDeleteButton={!readOnly}
      detectedFile={fileItems?.[0]?.fileType || null}
    />
  </div>
);

const TextContentView = ({
  localValue,
  readOnly,
  handleTextareaChange,
  handleTextareaBlur,
}: {
  localValue: string;
  readOnly: boolean;
  handleTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleTextareaBlur: () => void;
}) => (
  <Textarea
    placeholder="Value"
    readOnly={readOnly}
    className="max-h-[500px] min-h-[500px] overflow-y-auto font-mono"
    value={localValue}
    onChange={handleTextareaChange}
    onBlur={readOnly ? undefined : handleTextareaBlur}
  />
);

const ProcessingFileView = () => <div className="animate-pulse rounded-md bg-gray-100 p-2" />;

const DialogContentView = ({
  isFileContent,
  fileItems,
  isProcessingFile,
  isPopoverOpen,
  setIsPopoverOpen,
  onSaveFile,
  onDeleteFile,
  onAddFiles,
  readOnly,
  localValue,
  handleTextareaChange,
  handleTextareaBlur,
  popoverPortalContainer,
}: {
  isFileContent: boolean;
  fileItems: DetectedFileItem[] | null;
  isProcessingFile: boolean;
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
  onSaveFile?: (newData: string, previousData: string) => void;
  onDeleteFile?: (index: number) => void;
  onAddFiles?: (fileUrls: string[]) => void;
  readOnly: boolean;
  localValue: string;
  handleTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleTextareaBlur: () => void;
  popoverPortalContainer?: HTMLElement | null;
}) => {
  if (isFileContent) {
    if (fileItems && fileItems.length > 0) {
      return (
        <MultipleFilesView
          isPopoverOpen={isPopoverOpen}
          setIsPopoverOpen={setIsPopoverOpen}
          onSaveFile={onSaveFile}
          fileItems={fileItems}
          onDeleteFile={onDeleteFile}
          onAddFiles={onAddFiles}
          readOnly={readOnly}
          popoverPortalContainer={popoverPortalContainer}
        />
      );
    }
    return <SingleFileView fileItems={fileItems} onDeleteFile={onDeleteFile} readOnly={readOnly} />;
  }

  if (isProcessingFile) {
    return <ProcessingFileView />;
  }

  return (
    <TextContentView
      localValue={localValue}
      readOnly={readOnly}
      handleTextareaChange={handleTextareaChange}
      handleTextareaBlur={handleTextareaBlur}
    />
  );
};

interface MaximizeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  variable: Variable;
  onChange: (variable: Variable) => void;
  isFileContent: boolean;
  isProcessingFile: boolean;
  fileItems: DetectedFileItem[] | null;
  isPopoverOpen: boolean;
  setIsPopoverOpen: (open: boolean) => void;
  onAddFiles?: (fileUrls: string[]) => void;
  onSaveFile?: (newData: string, previousData: string) => void;
  onDeleteFile?: (index: number) => void;
  readOnly?: boolean;
  popoverPortalContainer?: HTMLElement | null;
}

const MaximizeDialog = ({
  isOpen,
  onOpenChange,
  variable,
  onChange,
  isFileContent,
  isProcessingFile,
  fileItems,
  isPopoverOpen,
  setIsPopoverOpen,
  onAddFiles,
  onSaveFile,
  onDeleteFile,
  readOnly = false,
  popoverPortalContainer,
}: MaximizeDialogProps) => {
  // Local state for editing - keeps raw value during editing
  const [localValue, setLocalValue] = useState("");
  const [dialogContentElement, setDialogContentElement] = useState<HTMLDivElement | null>(null);

  // Sync local value when variable changes from outside
  useEffect(() => {
    const displayValue =
      typeof variable.value === "object"
        ? JSON.stringify(variable.value, null, 2)
        : String(variable.value);
    setLocalValue(displayValue);
  }, [variable.value, isOpen]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Update local state only, don't commit to parent yet
    setLocalValue(e.target.value);
  };

  const handleTextareaBlur = () => {
    // Only parse and format on blur
    let valueToSave = localValue;

    // Try to parse and format JSON
    if (isValidJSON(localValue)) {
      valueToSave = formatJSON(localValue);
    }

    // Commit to parent
    onChange({ ...variable, value: valueToSave });
  };

  const handleDone = () => {
    // Ensure any unsaved changes are committed
    handleTextareaBlur();
    // Close the dialog
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent ref={setDialogContentElement} className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-mono">
            {variable.key || "Unnamed Variable"}
            {fileItems && fileItems.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({fileItems.length} files)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogContentView
          isFileContent={isFileContent}
          fileItems={fileItems}
          isProcessingFile={isProcessingFile}
          isPopoverOpen={isPopoverOpen}
          setIsPopoverOpen={setIsPopoverOpen}
          onSaveFile={onSaveFile}
          onDeleteFile={onDeleteFile}
          onAddFiles={onAddFiles}
          readOnly={readOnly}
          localValue={localValue}
          handleTextareaChange={handleTextareaChange}
          handleTextareaBlur={handleTextareaBlur}
          popoverPortalContainer={dialogContentElement ?? popoverPortalContainer}
        />
        <div className="flex justify-end">
          <Button onClick={handleDone}>
            <CheckIcon className="mr-2 h-4 w-4" />
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MaximizeDialog;
